<template>
	<div class="table-overflow-hide">
		<div class="directoryTable scrollable-55vh">
			<!-- Show table when not loading and no error -->
			<div v-if="!state.loading && !state.error" style="height: 100%">
				<table v-if="paginatedResults.length > 0" class="table" data-cy="classes-table">
					<thead class="sticky-header">
						<tr>
							<th class="icon-col">
								<uncheckbox
									:model-value="
										filteredData.length > 0 && filteredData.every((cls) => cls.excluded)
									"
									@update:model-value="
										(val) => filteredData.forEach((cls) => (cls.excluded = val))
									"
									:disabled="filteredData.length === 0 || !canEditDirectory"
								></uncheckbox>
							</th>
							<SortHeader
								title="Title"
								:asc="currentSort.asc"
								:sort-key="currentSort.key"
								column-sort-key="title"
								@click="() => setSort('title')"
								data-cy="classes-header-title"
								class="header-cell"
								color="#695b59"
								weight="500"
							></SortHeader>
							<SortHeader
								title="Course Code"
								:asc="currentSort.asc"
								:sort-key="currentSort.key"
								column-sort-key="course_code"
								@click="() => setSort('course_code')"
								data-cy="classes-header-course-code"
								class="header-cell"
								color="#695b59"
								weight="500"
							></SortHeader>
							<SortHeader
								:title="
									schoolStore.school?.integration === 'blackbaud'
										? 'Grade Level'
										: 'Subject'
								"
								:asc="currentSort.asc"
								:sort-key="currentSort.key"
								column-sort-key="subject"
								@click="() => setSort('subject')"
								data-cy="classes-header-subject"
								class="header-cell"
								color="#695b59"
								weight="500"
							></SortHeader>
							<SortHeader
								title="Class Code"
								:asc="currentSort.asc"
								:sort-key="currentSort.key"
								column-sort-key="class_code"
								@click="() => setSort('class_code')"
								data-cy="classes-header-class-code"
								class="header-cell"
								color="#695b59"
								weight="500"
							></SortHeader>
							<SortHeader
								title="Course Title"
								:asc="currentSort.asc"
								:sort-key="currentSort.key"
								column-sort-key="course_title"
								@click="() => setSort('course_title')"
								data-cy="classes-header-course-title"
								class="header-cell"
								color="#695b59"
								weight="500"
							></SortHeader>
							<SortHeader
								v-if="schoolStore.school?.integration === 'blackbaud'"
								title="Location"
								:asc="currentSort.asc"
								:sort-key="currentSort.key"
								column-sort-key="location"
								@click="() => setSort('location')"
								data-cy="classes-header-location"
								class="header-cell"
								color="#695b59"
								weight="500"
							></SortHeader>
							<th class="icon-col">
								<v-btn
									@click="fetchClasses"
									icon
									size="x-small"
									color="gray200"
									:loading="state.loading"
									style="max-width: 20px; max-height: 20px"
									data-cy="classes-reload-btn"
								>
									<v-icon>mdi-reload</v-icon></v-btn
								>
							</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="row in paginatedResults"
							:key="row.class_id"
							:class="{
								'table-row': true,
								selected: editRelationModal.show && editRelationModal.cls === row,
							}"
						>
							<td>
								<uncheckbox v-model="row.excluded" :disabled="!canEditDirectory"></uncheckbox>
							</td>
							<td data-cy="table-col-title" :class="{ strikethrough: row.excluded }">
								{{ row.title }}
							</td>
							<td data-cy="table-col-course-code" :class="{ strikethrough: row.excluded }">
								{{ row.course_code }}
							</td>
							<td data-cy="table-col-subject" :class="{ strikethrough: row.excluded }">
								{{ row.subject }}
							</td>
							<td data-cy="table-col-class-code" :class="{ strikethrough: row.excluded }">
								{{ row.class_code }}
							</td>
							<td data-cy="table-col-course-title" :class="{ strikethrough: row.excluded }">
								{{ row.course_title }}
							</td>
							<td
								v-if="schoolStore.school?.integration === 'blackbaud'"
								data-cy="table-col-location"
								:class="{ strikethrough: row.excluded }"
							>
								{{ row.location }}
							</td>
							<td tabindex="1">
								<v-btn
									@click="
										editRelationModal.show = true;
										editRelationModal.cls = row;
									"
									color="gray300"
									density="compact"
									icon
									variant="text"
									:disabled="deleted || !canEditDirectory"
									data-cy="class-relations-btn"
								>
									<v-icon color="gray500">mdi-account-group</v-icon>
								</v-btn>
							</td>
						</tr>
					</tbody>
				</table>

				<!-- Empty state when no classes but not loading -->
				<div
					v-if="!paginatedResults.length"
					style="
						height: calc(100% - 60px);
						background-color: rgba(246, 247, 237, 0.5);
						border-radius: 15px;
					"
				>
					<div class="empty-state">
						<figure style="max-width: 300px">
							<IconListEmptyState />
						</figure>
						<div class="title">No Classes</div>
						<p>
							Classes can only be added through certain integrations and are not necessary
							otherwise
						</p>
					</div>
				</div>
			</div>

			<!-- Loading state -->
			<div v-else-if="state.loading" class="loading-splash">
				<LoadingState text="Loading Classes"></LoadingState>
			</div>

			<!-- Error state -->
			<div v-else class="empty-state-container">
				<div class="empty-state">
					<figure style="max-width: 300px">
						<IconListEmptyState />
					</figure>
					<div class="title">Error loading classes.</div>
					<p>The classes feature may not be available yet.</p>
					<div class="mt-3">
						<v-btn color="primary" @click="fetchClasses">Retry</v-btn>
					</div>
				</div>
			</div>
		</div>
	</div>
	<div class="table-pagination-btns">
		<pagination v-if="!state.error" v-bind="forComponent()"></pagination>
		<v-btn
			v-if="canEditDirectory && !deleted"
			:loading="state.loadingSave"
			@click="saveAction"
			data-cy="directory-classes-save-exclude-btn"
			>Exclude selected classes</v-btn
		>
		<v-btn
			v-else-if="canEditDirectory"
			:loading="state.loadingSave"
			@click="saveAction"
			data-cy="directory-classes-save-restore-btn"
			>Restore unselected classes</v-btn
		>
	</div>
	<class-relations-modal
		v-model:show="editRelationModal.show"
		:directory-entity="editRelationModal.cls"
		:entity-type="'class'"
		:entity-id="String(editRelationModal.cls.class_id)"
		:entity-name="`${editRelationModal.cls.title}`"
		:tabs="['student', 'teacher']"
		@entity-click="handleEntityClick"
	></class-relations-modal>
</template>

<script setup lang="ts">
import { reactive, computed, onMounted, watch } from "vue";
import usePaginate from "@/hooks/usePaginate";
import pagination from "@/components/pagination.vue";
import { complexSearch } from "@/utils";
import type { ClassesType } from "@/api_types/school";
import ClassRelationsModal from "@/components/school/directory/RelationshipModal.vue";
// import { useAlertsStore } from "@/stores/alerts";
import LoadingState from "@/components/loadingState.vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
// import FetchService from "@/services/fetch.service";
import useTableSort from "@/hooks/useTableSort";
import { sort } from "@/utils/sort";
import SortHeader from "@/components/global/sortHeader.vue";
import useClasses from "@/stores/school/classes";
import uncheckbox from "@/components/global/uncheckbox.vue";
import { useAuthenticationStore } from "@/stores/authentication";
import { useSchoolStore } from "@/stores/school";

const props = defineProps<{
	tab: "teacher" | "parent" | "student" | "team" | "class";
	search: string;
	deleted: boolean;
	selectedEntity?: any;
}>();

const emit = defineEmits<{
	(e: "update:search", ...args: any[]): void;
	(e: "entity-click", entityData: any): void;
}>();

const authStore = useAuthenticationStore();
const schoolStore = useSchoolStore();

const canEditDirectory = computed(() => authStore.hasPolicy("school.directory.classes.write"));

const { currentSort, setSort } = useTableSort([
	{
		key: "title",
		title: "Title",
		sortKey: "title",
	},
	{
		key: "course_code",
		title: "Course Code",
		sortKey: "course_code",
	},
	{
		key: "subject",
		title: "Subject",
		sortKey: "subject",
	},
	{
		key: "class_code",
		title: "Class Code",
		sortKey: "class_code",
	},
	{
		key: "course_title",
		title: "Course Title",
		sortKey: "course_title",
	},
	{
		key: "location",
		title: "Location",
		sortKey: "location",
	},
]);

const state = reactive({
	classes: [] as ClassesType,
	loadingPins: [] as string[],
	loadingDownload: false,
	loading: false,
	loadingSave: false,
	error: false,
});

const editRelationModal = reactive({ show: false, cls: {} as ClassesType[number] });
// const uploadModal = reactive({ show: false });
const classesStore = useClasses();
async function saveAction() {
	try {
		state.loadingSave = true;
		if (props.deleted) {
			// Restore unselected classes
			await classesStore.include(paginatedResults.value.filter((cls) => !cls.excluded));
		} else {
			// Exclude selected classes
			await classesStore.exclude();
		}
		// await classesStore.exclude();
	} catch (error) {
		console.error("Error in saveAction:", error);
	} finally {
		state.loadingSave = false;
	}
}

async function fetchClasses() {
	try {
		state.loading = true;
		state.error = false;
		await classesStore.fetch();
		state.error = !!classesStore.error;
	} catch (error) {
		console.error("Error in fetchClasses:", error);
		state.error = true;
	} finally {
		state.loading = false;
	}
}

onMounted(() => {
	fetchClasses();
});

watch(
	() => props.tab,
	(newTab) => {
		if (newTab === "class") {
			fetchClasses();
		}
	}
);

const filteredData = computed(() => {
	const searchTerm = props.search?.trim() || "";
	// const isDeleted = props.deleted;

	const filtered = (classesStore.data ?? []).filter((cls) => {
		// Only include classes matching the deleted status
		if (cls.deleted !== props.deleted) return false;

		// Build a string of searchable fields
		const haystack = [
			cls.title,
			cls.course_title,
			cls.class_code,
			cls.course_code,
			cls.location,
			cls.subject,
		]
			.filter(Boolean)
			.join(" ")
			.toLowerCase();

		// Match search term if provided
		return !searchTerm || complexSearch(searchTerm, haystack);
	});

	// Sort the filtered data based on current sort configuration
	const sortKey = currentSort.value.key as keyof ClassesType[number];
	const asc = currentSort.value.asc;
	
	
	const sorted = sort(
		filtered,
		(i) => {
			// For title sorting, use multi-level sort: title first, then class_code
			if (sortKey === "title") {
				const title = i.title?.toLowerCase() || "";
				const classCode = i.class_code?.toString() || "";
				// Create a sortable string that combines title and class_code
				// Pad class_code with zeros to ensure proper numeric sorting
				const paddedClassCode = classCode.padStart(10, '0');
				return `${title}${paddedClassCode}`;
			}
			
			// For other fields, use the original logic
			const value = i[sortKey];
			if (typeof value === "string") {
				return value.toLowerCase();
			}
			return value || "";
		},
		asc
	);
	
	
	return sorted;
});

const { paginatedResults, pageStatus, totalPages, forComponent } = usePaginate(filteredData);


// Handle entity clicks from relationship modal
const handleEntityClick = (entityData: any) => {
	emit('entity-click', entityData);
};

// Watch for selected entity changes and open the relationship modal
watch(() => props.selectedEntity, (newEntity) => {
	if (newEntity && newEntity.entityType === 'class') {
		// Find the class in the current data
		// Handle both string and number comparisons for class_id
		const cls = classesStore.data.find(c => 
			String(c.class_id) === String(newEntity.entityId) || 
			c.class_id === newEntity.entityId ||
			(c as any).uid === newEntity.entityId
		);
		if (cls) {
			editRelationModal.cls = cls;
			editRelationModal.show = true;
		}
	}
}, { immediate: true });
</script>
<style scoped lang="scss">
.strikethrough {
	text-decoration: line-through;
	color: #aaa;
}
</style>
